/**
 * Opportunity Import Utilities with Smart Customer Matching
 * 
 * Enables bulk import of opportunities with:
 * - Fuzzy Customer Name matching (handles misspellings)
 * - Auto-creation of placeholder customers for new leads
 * - Flagging for review and deduplication
 * - Error handling and batch processing
 */

import { collection, addDoc, serverTimestamp, query, where, getDocs, doc, runTransaction, setDoc, getDoc } from 'firebase/firestore';
import Fuse from 'fuse.js';
import { db } from './firebase';
import { collectionPath } from './firestoreCollections';
import { sanitizeString, sanitizeEmail, sanitizeNumber, sanitizeDate } from './sanitization';
import { logEntityChanges } from './logger';
import { convertDateForSave } from './dateHelpers';
import { generateOpportunityId, generateLeadSourceId } from './documentIdUtils';
import { VALUE_RANGES } from '../components/forms/OppModal';

/**
 * Format date as MM/DD/YYYY for template display
 */
function formatDateMMDDYYYY(date) {
  const d = new Date(date);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

/**
 * Convert a numeric value or range string to a valid range key
 * @param {string|number} input - Numeric value or range key
 * @returns {string} Valid range key (e.g., '5000-10000')
 */
function normalizeValueToRange(input) {
  if (!input && input !== 0) return '0-1000'; // Default to lowest range
  
  const inputStr = String(input).trim();
  
  // Check if it's already a valid range key
  if (VALUE_RANGES.find(r => r.value === inputStr)) {
    return inputStr;
  }
  
  // Try to parse as number and find matching range
  const numValue = parseFloat(inputStr.replace(/[$,]/g, ''));
  if (!isNaN(numValue)) {
    // Find the range that contains this value (skip legacy entries)
    for (const range of VALUE_RANGES) {
      if (!range.legacy && numValue >= range.min && numValue < range.max) {
        return range.value;
      }
    }
  }
  
  return '0-1000'; // Default fallback
}

/**
 * Calculate string similarity using Levenshtein distance
 * Returns a value between 0 (completely different) and 1 (identical)
 * 
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @returns {number} - Similarity score (0-1)
 */
function calculateStringSimilarity(str1, str2) {
  const s1 = str1.toLowerCase();
  const s2 = str2.toLowerCase();
  
  if (s1 === s2) return 1.0;
  if (s1.length === 0 || s2.length === 0) return 0.0;
  
  // Levenshtein distance implementation
  const matrix = [];
  
  for (let i = 0; i <= s2.length; i++) {
    matrix[i] = [i];
  }
  
  for (let j = 0; j <= s1.length; j++) {
    matrix[0][j] = j;
  }
  
  for (let i = 1; i <= s2.length; i++) {
    for (let j = 1; j <= s1.length; j++) {
      if (s2.charAt(i - 1) === s1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }
  
  const maxLength = Math.max(s1.length, s2.length);
  const distance = matrix[s2.length][s1.length];
  return 1 - (distance / maxLength);
}

/**
 * List of common consumer email domains that should not be used for fuzzy matching
 * These domains are too generic and lead to false positive matches
 */
const CONSUMER_EMAIL_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
  'icloud.com', 'live.com', 'msn.com', 'mail.com', 'protonmail.com',
  'ymail.com', 'rocketmail.com', 'me.com', 'mac.com', 'comcast.net',
  'verizon.net', 'att.net', 'sbcglobal.net', 'cox.net', 'bellsouth.net',
  'earthlink.net', 'charter.net', 'optonline.net', 'juno.com', 'netzero.net'
];

/**
 * Check if an email domain is a consumer/personal email domain
 * @param {string} email - Email address to check
 * @returns {boolean} - True if it's a consumer domain
 */
function isConsumerEmail(email) {
  if (!email || typeof email !== 'string') return false;
  const emailLower = email.toLowerCase().trim();
  const domainMatch = emailLower.match(/@(.+)$/);
  if (!domainMatch || !domainMatch[1]) return false;
  
  const domain = domainMatch[1];
  return CONSUMER_EMAIL_DOMAINS.includes(domain);
}

/**
 * Find customers by EXACT match on any of 6 fields (100% matches only)
 * 
 * MATCHING PRIORITY ORDER (most reliable to least reliable):
 * 1. Customer email - unique company identifier
 * 2. Contact email - globally unique, most reliable cross-reference
 * 3. Customer phone - unique identifier
 * 4. Contact phone - unique identifier  
 * 5. Customer name - exact text match
 * 6. Contact name - ONLY if customer name also matches (names are not unique)
 * 
 * @param {string} customerName - Customer name to search for
 * @param {string} customerEmail - Customer email to search for
 * @param {string} customerPhone - Customer phone to search for
 * @param {string} contactName - Contact name to search for
 * @param {string} contactEmail - Contact email to search for
 * @param {string} contactPhone - Contact phone to search for
 * @param {Array} existingCustomers - Array of customer objects with id, customerName, customerEmail, customerPhone, contacts[]
 * @returns {Object|null} - Matching customer or null (only 100% matches)
 */
export function findExactCustomerMatch(customerName, customerEmail, customerPhone, contactName, contactEmail, contactPhone, existingCustomers, standaloneContacts = []) {
  if (!existingCustomers || existingCustomers.length === 0) return null;

  console.log('🔍 Looking for exact match (priority: email > phone > name):', {
    customerName,
    customerEmail,
    customerPhone,
    contactName,
    contactEmail,
    contactPhone,
    customersCount: existingCustomers.length
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // PRIORITY 1: Customer Email (most unique identifier for a company)
  // ═══════════════════════════════════════════════════════════════════════════
  if (customerEmail && customerEmail.trim().length > 0) {
    const emailLower = customerEmail.trim().toLowerCase();
    const emailMatch = existingCustomers.find(c => 
      c.customerEmail && c.customerEmail.toLowerCase() === emailLower
    );
    if (emailMatch) {
      console.log('✅ MATCH via CUSTOMER EMAIL:', emailMatch.customerName);
      return {
        customer: emailMatch,
        confidence: 100,
        matchType: 'exact_customer_email'
      };
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRIORITY 2: Contact Email (globally unique - MOST RELIABLE for finding right customer)
  // Check standalone contacts collection FIRST, then fall back to embedded arrays
  // ═══════════════════════════════════════════════════════════════════════════
  if (contactEmail && contactEmail.trim().length > 0) {
    const contactEmailLower = contactEmail.trim().toLowerCase();

    // 2a. Standalone contacts collection (primary)
    for (const contact of standaloneContacts) {
      if (contact.email && contact.email.toLowerCase() === contactEmailLower && contact.customerId) {
        const customer = existingCustomers.find(c => c.id === contact.customerId);
        if (customer) {
          console.log('✅ MATCH via STANDALONE CONTACT EMAIL:', customer.customerName, '| Contact:', contactEmail);
          console.log('   (CSV customer name was:', customerName, ')');
          return { customer, confidence: 100, matchType: 'exact_contact_email' };
        }
      }
    }

    // 2b. Fallback: embedded contacts arrays on customer documents
    for (const customer of existingCustomers) {
      if (!Array.isArray(customer.contacts) || customer.contacts.length === 0) continue;
      
      const contactMatch = customer.contacts.find(contact => 
        contact.email && contact.email.trim().length > 0 && contact.email.toLowerCase() === contactEmailLower
      );
      
      if (contactMatch) {
        console.log('✅ MATCH via EMBEDDED CONTACT EMAIL:', customer.customerName, '| Contact:', contactEmail);
        console.log('   (CSV customer name was:', customerName, ')');
        return { customer, confidence: 100, matchType: 'exact_contact_email' };
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRIORITY 3: Customer Phone (unique identifier)
  // ═══════════════════════════════════════════════════════════════════════════
  if (customerPhone && customerPhone.trim().length > 0) {
    const normalizedSearch = customerPhone.replace(/\D/g, '');
    if (normalizedSearch.length >= 10) {
      const phoneMatch = existingCustomers.find(c => {
        if (!c.customerPhone || c.customerPhone.trim().length === 0) return false;
        const normalizedCustomer = c.customerPhone.replace(/\D/g, '');
        return normalizedCustomer.length >= 10 && normalizedCustomer === normalizedSearch;
      });
      if (phoneMatch) {
        console.log('✅ MATCH via CUSTOMER PHONE:', phoneMatch.customerName);
        return {
          customer: phoneMatch,
          confidence: 100,
          matchType: 'exact_customer_phone'
        };
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRIORITY 4: Contact Phone (unique identifier)
  // Check standalone contacts collection FIRST, then fall back to embedded arrays
  // ═══════════════════════════════════════════════════════════════════════════
  if (contactPhone && contactPhone.trim().length > 0) {
    const normalizedSearchPhone = contactPhone.replace(/\D/g, '');
    
    if (normalizedSearchPhone.length >= 10) {
      // 4a. Standalone contacts collection (primary)
      for (const contact of standaloneContacts) {
        if (!contact.phone || !contact.customerId) continue;
        const normalizedContactPhone = contact.phone.replace(/\D/g, '');
        if (normalizedContactPhone.length >= 10 && normalizedContactPhone === normalizedSearchPhone) {
          const customer = existingCustomers.find(c => c.id === contact.customerId);
          if (customer) {
            console.log('✅ MATCH via STANDALONE CONTACT PHONE:', customer.customerName, '| Contact phone:', contactPhone);
            return { customer, confidence: 100, matchType: 'exact_contact_phone' };
          }
        }
      }

      // 4b. Fallback: embedded contacts arrays
      for (const customer of existingCustomers) {
        if (!Array.isArray(customer.contacts)) continue;
        
        const contactMatch = customer.contacts.find(contact => {
          if (!contact.phone || contact.phone.trim().length === 0) return false;
          const normalizedContactPhone = contact.phone.replace(/\D/g, '');
          return normalizedContactPhone.length >= 10 && normalizedContactPhone === normalizedSearchPhone;
        });
        
        if (contactMatch) {
          console.log('✅ MATCH via EMBEDDED CONTACT PHONE:', customer.customerName, '| Contact phone:', contactPhone);
          return { customer, confidence: 100, matchType: 'exact_contact_phone' };
        }
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRIORITY 5: Customer Name (exact text match)
  // Only checked AFTER email/phone - allows email to override wrong customer name
  // ═══════════════════════════════════════════════════════════════════════════
  if (customerName && customerName.trim().length > 0) {
    const nameLower = customerName.trim().toLowerCase();
    const nameMatch = existingCustomers.find(c => 
      c.customerName?.toLowerCase() === nameLower
    );
    if (nameMatch) {
      console.log('✅ MATCH via CUSTOMER NAME:', nameMatch.customerName);
      return {
        customer: nameMatch,
        confidence: 100,
        matchType: 'exact_customer_name'
      };
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRIORITY 6: Contact Name (ONLY if customer name ALSO matches)
  // Names are NOT unique - "John Smith" exists at many companies
  // Check standalone contacts collection FIRST, then fall back to embedded arrays
  // ═══════════════════════════════════════════════════════════════════════════
  if (contactName && contactName.trim().length > 0 && customerName && customerName.trim().length > 0) {
    const contactNameLower = contactName.trim().toLowerCase();
    const customerNameLower = customerName.trim().toLowerCase();
    
    console.log('🔍 Searching for contact name:', contactNameLower, 'at customer:', customerNameLower);

    // 6a. Standalone contacts collection (primary)
    for (const contact of standaloneContacts) {
      if (!contact.customerId) continue;
      const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim().toLowerCase();
      if (fullName.length === 0 || fullName !== contactNameLower) continue;
      const customer = existingCustomers.find(c => c.id === contact.customerId);
      if (customer && (customer.customerName || '').trim().toLowerCase() === customerNameLower) {
        console.log('✅ MATCH via STANDALONE CONTACT NAME:', customer.customerName, '| Contact:', contactName);
        return { customer, confidence: 100, matchType: 'exact_contact_name' };
      }
    }

    // 6b. Fallback: embedded contacts arrays
    for (const customer of existingCustomers) {
      if (!Array.isArray(customer.contacts) || customer.contacts.length === 0) continue;
      
      // MUST MATCH: Customer name must match before checking contacts by name
      const existingCustomerNameLower = (customer.customerName || '').trim().toLowerCase();
      if (existingCustomerNameLower !== customerNameLower) {
        continue; // Skip - customer name doesn't match
      }
      
      const contactMatch = customer.contacts.find(contact => {
        const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim().toLowerCase();
        return fullName.length > 0 && contactNameLower.length > 0 && fullName === contactNameLower;
      });
      
      if (contactMatch) {
        console.log('✅ MATCH via EMBEDDED CONTACT NAME:', customer.customerName, '| Contact:', contactName);
        return { customer, confidence: 100, matchType: 'exact_contact_name' };
      }
    }
  }

  // No 100% match found - return null to create temp customer for review
  console.log('❌ No exact match found - will create temp customer for review');
  return null;
}

/**
 * REMOVED: Fuzzy matching and contact email matching
 * These are now handled by generating match suggestions in Customer Review UI
 * Only 100% certain matches (exact customer email/phone) auto-match
 */

/**
 * Capitalize Customer Name properly (Title Case)
 * Handles special cases like "LLC", "Inc.", "Country Club", etc.
 * 
 * @param {string} customerName - Customer Name to capitalize
 * @returns {string} - Properly capitalized Customer Name
 */
function capitalizeCustomerName(customerName) {
  if (!customerName || typeof customerName !== 'string') {
    return '';
  }

  // Words that should stay lowercase (unless at start)
  const lowercase = ['and', 'or', 'the', 'of', 'at', 'by', 'for', 'in', 'on', 'to', 'with'];
  
  // Words that should be all caps
  const uppercase = ['llc', 'lp', 'inc', 'corp', 'usa', 'us', 'uk'];

  const words = customerName.trim().split(/\s+/);
  
  return words.map((word, index) => {
    const lowerWord = word.toLowerCase();
    
    // Check if it's an uppercase abbreviation
    if (uppercase.includes(lowerWord)) {
      return word.toUpperCase();
    }
    
    // Preserve words that are already all caps (likely acronyms like ABC, PGA, etc.)
    // Only if they're 2-5 letters and don't contain lowercase
    if (word.length >= 2 && word.length <= 5 && word === word.toUpperCase() && /^[A-Z]+$/.test(word)) {
      return word;
    }
    
    // Check if it should stay lowercase (not at start)
    if (index > 0 && lowercase.includes(lowerWord)) {
      return lowerWord;
    }
    
    // Handle words with periods (like "Inc.")
    if (word.includes('.')) {
      return word.split('.').map(part => 
        part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
      ).join('.');
    }
    
    // Default: capitalize first letter
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }).join(' ');
}

/**
 * Parse full name into first and last name
 * Handles various formats: "John Doe", "Doe, John", "John", etc.
 * 
 * @param {string} fullName - Full name string
 * @returns {Object} - {firstName, lastName}
 */
function parseFullName(fullName) {
  if (!fullName || typeof fullName !== 'string') {
    return { firstName: '', lastName: '' };
  }

  const trimmed = fullName.trim();
  
  // Handle "Last, First" format
  if (trimmed.includes(',')) {
    const [lastName, firstName] = trimmed.split(',').map(s => s.trim());
    return { firstName: firstName || '', lastName: lastName || '' };
  }
  
  // Handle "First Last" format
  const parts = trimmed.split(/\s+/);
  if (parts.length === 0) {
    return { firstName: '', lastName: '' };
  } else if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  } else {
    // Take first part as firstName, rest as lastName
    const firstName = parts[0];
    const lastName = parts.slice(1).join(' ');
    return { firstName, lastName };
  }
}

/**
 * Create contact for imported opportunity
 * Stores contact in the customer's contacts array (NOT a separate collection)
 * 
 * @param {Object} row - CSV row data with contact info
 * @param {string} customerId - Customer ID to associate contact with
 * @param {Object} user - Current user object
 * @returns {boolean} - True if contact was created, false if skipped or exists
 */
async function createContactFromImport(row, customerId, user) {
  // Use ONLY new column names - no backward compatibility
  const fullName = row['Contact Name']?.trim();
  
  if (!fullName) {
    return false; // No contact info provided
  }

  const sanitizedFullName = sanitizeString(fullName, 200);
  const contactTitle = sanitizeString(row['Contact Title'] || '', 100);
  const contactEmail = sanitizeEmail(row['Contact Email'] || '');
  const contactPhone = sanitizeString(row['Contact Phone'] || '', 20);
  const contactFocusRaw = row['Contact Focus']?.trim() || '';

  if (!contactEmail) {
    console.log(`⚠️ Skipping contact "${sanitizedFullName}" - no email provided`);
    return false; // Email is required to prevent duplicates
  }

  // Split name into firstName and lastName (matches CustomerDetailPage schema)
  const nameParts = sanitizedFullName.split(' ').filter(Boolean);
  let firstName = '';
  let lastName = '';
  
  if (nameParts.length === 1) {
    firstName = nameParts[0];
  } else if (nameParts.length >= 2) {
    // First word is firstName, rest is lastName
    firstName = nameParts[0];
    lastName = nameParts.slice(1).join(' ');
  }

  // Parse contactFocus into array (comma-separated or single value)
  let contactFocusArr = [];
  if (contactFocusRaw) {
    contactFocusArr = contactFocusRaw.split(',').map(s => s.trim()).filter(Boolean);
  }

  try {
    const { getDoc, setDoc, getDocs: getDocs2, query: query2, where: where2 } = await import('firebase/firestore');

    // Check if contact already exists in standalone contacts collection (by email + customer)
    if (contactEmail) {
      const existingQ = query2(
        collection(db, collectionPath('contacts')),
        where2('customerId', '==', customerId),
        where2('email', '==', contactEmail)
      );
      const existingSnap = await getDocs2(existingQ);
      if (!existingSnap.empty) {
        console.log(`ℹ️ Contact already exists for customer: ${sanitizedFullName} (${contactEmail})`);
        return existingSnap.docs[0].id; // Return existing contact ID
      }
    }

    // Look up customer name for denormalization
    let customerName = '';
    try {
      const customerDocRef = doc(db, collectionPath('customers'), customerId);
      const customerSnap = await getDoc(customerDocRef);
      if (customerSnap.exists()) {
        customerName = customerSnap.data().customerName || '';
      }
    } catch (err) {
      console.warn('Could not fetch customer name for contact:', err);
    }

    // Create contact in standalone contacts collection (matches CustomerDetailPage schema)
    const contactId = Date.now().toString();
    const payload = {
      contactId: contactId,
      contactCustomerId: customerId,
      customerId: customerId,
      customerName: customerName,
      firstName: firstName,
      lastName: lastName,
      email: contactEmail,
      phone: contactPhone,
      contactPosition: contactTitle,
      contactFocus: contactFocusArr,
      isPrimary: '0',
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      sourceCreation: 'import',
      importedFrom: 'opportunity_import'
    };

    const contactRef = doc(db, collectionPath('contacts'), contactId);
    await setDoc(contactRef, payload);
    
    console.log(`✅ Added contact to contacts collection: ${sanitizedFullName} (${contactEmail || 'no email'}) for customer ${customerId}`);
    
    // Log contact creation
    await logEntityChanges({
      oldObj: null,
      newObj: payload,
      fields: ['firstName', 'lastName', 'email', 'phone', 'contactPosition', 'contactFocus'],
      entityType: 'Contact',
      entityId: contactId,
      user: user,
      action: 'Created',
      extraDetails: `Contact "${sanitizedFullName}" added to customer "${customerName}" via import`
    });

    return contactId; // Return the new contact ID
  } catch (error) {
    console.error('Error creating contact:', error);
    // Don't throw - contact creation is optional
    return false;
  }
}

/**
 * Generate a proper Customer Name from an email domain
 * Attempts to convert domain into a readable Customer Name
 * 
 * @param {string} baseDomain - Base domain without TLD (e.g., "meridiangolfclub" from "meridiangolfclub.com")
 * @returns {string|null} - Generated Customer Name or null if domain is generic/invalid
 * 
 * @example
 * generatecustomerNameFromDomain("meridiangolfclub") → "Meridian Golf Club"
 * generatecustomerNameFromDomain("st-andrews-cc") → "St Andrews Cc"
 * generatecustomerNameFromDomain("gmail") → null (generic provider)
 */
function generatecustomerNameFromDomain(baseDomain) {
  if (!baseDomain || baseDomain.length < 3) return null;
  
  // Skip generic email providers - they don't represent Customer Names
  const genericDomains = [
    'gmail', 'yahoo', 'outlook', 'hotmail', 'aol', 'icloud', 
    'msn', 'live', 'mail', 'ymail', 'protonmail', 'zoho',
    'comcast', 'verizon', 'att', 'sbcglobal'
  ];
  
  const domainLower = baseDomain.toLowerCase();
  if (genericDomains.some(generic => domainLower.includes(generic))) {
    return null; // Can't derive Customer Name from personal email providers
  }
  
  // Strip common prefixes that are often concatenated (the, a, an)
  // e.g., "theglacierclub" → "glacierclub"
  let cleanedDomain = baseDomain;
  const prefixPattern = /^(the|a|an)([a-z])/i;
  const prefixMatch = cleanedDomain.match(prefixPattern);
  if (prefixMatch) {
    // Only strip if followed by lowercase letter (indicates concatenation)
    cleanedDomain = prefixMatch[2] + cleanedDomain.slice(prefixMatch[0].length);
    console.log(`🔧 Stripped prefix from domain: "${baseDomain}" → "${cleanedDomain}"`);
  }
  
  // Split domain on common separators (hyphens, underscores, dots)
  let words = cleanedDomain.split(/[-_.]+/).filter(w => w.length > 0);
  
  // If domain is one word with no separators, try to split on camelCase
  if (words.length === 1) {
    // Try to detect word boundaries in concatenated domains
    // e.g., "meridiangolfclub" → ["meridian", "golf", "club"]
    const word = words[0];
    
    // Split on transitions from lowercase to uppercase (camelCase)
    let splitWords = word.split(/(?=[A-Z])/).filter(w => w.length > 0);
    
    // If no camelCase, try common known suffixes
    if (splitWords.length === 1) {
      const knownSuffixes = [
        'golfclub', 'countryclub', 'golf', 'club', 'resort', 'hotel',
        'academy', 'school', 'university', 'college', 'customer', 'inc',
        'corp', 'llc', 'group', 'shop', 'store', 'market'
      ];
      
      for (const suffix of knownSuffixes) {
        if (word.endsWith(suffix) && word.length > suffix.length) {
          const base = word.slice(0, -suffix.length);
          words = [base, suffix];
          break;
        }
      }
    } else {
      words = splitWords;
    }
  }
  
  // Capitalize each word with proper title casing
  const capitalized = words.map(word => {
    // Handle all-caps abbreviations (keep them uppercase if short)
    if (word.length <= 3 && word === word.toUpperCase()) {
      return word;
    }
    
    // Standard title case
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
  
  // Join with spaces
  let customerName = capitalized.join(' ');
  
  // Add common suffixes if detected
  const suffixMap = {
    'golfclub': 'Golf Club',
    'countryclub': 'Country Club',
    'cc': 'Country Club',
    'gc': 'Golf Club'
  };
  
  const lastWord = words[words.length - 1]?.toLowerCase();
  if (suffixMap[lastWord]) {
    // Replace last word with proper suffix
    const nameWords = capitalized.slice(0, -1);
    customerName = [...nameWords, suffixMap[lastWord]].join(' ');
  }
  
  // If we stripped "the" prefix and the result looks like it should have "The"
  // (e.g., "The Glacier Club" not just "Glacier Club"), add it back
  if (prefixMatch && prefixMatch[1].toLowerCase() === 'the') {
    // Add "The" back for proper names (usually with specific keywords)
    const nameKeywords = ['club', 'resort', 'hotel', 'group', 'customer', 'breakers', 'links'];
    const hasKeyword = nameKeywords.some(keyword => 
      customerName.toLowerCase().includes(keyword)
    );
    
    if (hasKeyword) {
      customerName = 'The ' + customerName;
      console.log(`🔧 Added "The" prefix back: "${customerName}"`);
    }
  }
  
  // Return null if result is too short or looks invalid
  if (customerName.length < 3 || /^\d+$/.test(customerName)) {
    return null;
  }
  
  return customerName;
}

/**
 * Create a placeholder customer from import row
 * Marks customer as needing review for later deduplication
 * 
 * @param {Object} row - CSV row data
 * @param {Object} user - Current user object
 * @param {string} salesRepName - Sales rep display name
 * @param {Object} locationData - Optional customer location data { customerCity, customerState, customerCountry }
 * @returns {string} - New customer document ID
 */
export async function createPlaceholderCustomer(row, user, salesRepName, locationData = {}) {
  const customerName = row['Customer Name']?.trim();
  
  if (!customerName) {
    throw new Error('Customer Name is required to create placeholder customer');
  }

  // Extract contact info from CSV for display in Customer Review
  const contactName = row['Contact Name']?.trim() || '';
  const contactTitle = row['Contact Title']?.trim() || '';
  const contactEmail = row['Contact Email']?.trim() || '';
  const contactPhone = row['Contact Phone']?.trim() || '';
  
  // Customer location fields (passed from import processor)
  const { customerCity = '', customerState = '', customerCountry = '' } = locationData;
  
  // Capitalize Customer Name properly
  const capitalizedName = capitalizeCustomerName(customerName);

  const newCustomer = {
    customerName: sanitizeString(capitalizedName, 200),
    customerEmail: '', // Customer email removed from import
    customerPhone: '', // Customer phone removed from import
    // Location fields (optional)
    ...(customerCity && { customerCity: sanitizeString(customerCity, 100) }),
    ...(customerState && { customerState: sanitizeString(customerState, 50) }),
    ...(customerCountry && { customerCountry: sanitizeString(customerCountry, 100) }),
    customerStatus: 'Lead', // Default status for imported customers
    needsReview: true, // Flag for review dashboard
    importedFrom: 'opportunity_import',
    importBatch: new Date().toISOString(),
    originalName: customerName, // Preserve original uncapitalized name for reference
    // Store contact info for display in Customer Review table
    importedContactName: sanitizeString(contactName, 200),
    importedContactTitle: sanitizeString(contactTitle, 100),
    importedContactEmail: sanitizeEmail(contactEmail),
    importedContactPhone: sanitizeString(contactPhone, 20),
    createdAt: serverTimestamp(),
    createdBy: user?.uid || '',
    // Add sales rep from opportunity
    salesReps: salesRepName ? [{
      salesRepName: salesRepName,
      displayName: salesRepName,
      splitPercent: 100
    }] : []
  };

  try {
    const docRef = await addDoc(
      collection(db, collectionPath('customers')),
      newCustomer
    );

    // Log customer creation
    await logEntityChanges({
      oldObj: null,
      newObj: { ...newCustomer, id: docRef.id },
      fields: ['customerName', 'customerEmail', 'customerPhone', 'customerStatus'],
      entityType: 'Customer',
      entityId: docRef.id,
      user: user,
      action: 'Created'
    });

    return docRef.id;
  } catch (error) {
    console.error('Error creating placeholder customer:', error);
    throw new Error(`Failed to create customer "${customerName}": ${error.message}`);
  }
}

/**
 * Find user by display name for sales rep assignment
 * 
 * @param {string} salesRepName - Sales rep name from CSV
 * @param {Array} users - Array of user objects
 * @returns {Object|null} - Matching user or null
 */
function findUserByName(salesRepName, users) {
  if (!salesRepName || !users || users.length === 0) return null;

  const nameLower = salesRepName.toLowerCase().trim();
  
  return users.find(u => 
    u.displayName?.toLowerCase().trim() === nameLower ||
    u.email?.toLowerCase().trim() === nameLower ||
    `${u.firstName} ${u.lastName}`.toLowerCase().trim() === nameLower
  );
}

/**
 * Parse date from various formats (MM/DD/YYYY, YYYY-MM-DD, ISO)
 * 
 * @param {string} dateStr - Date string in various formats
 * @returns {string|null} - Date in YYYY-MM-DD format or null
 */
function parseFlexibleDate(dateStr) {
  if (!dateStr || !dateStr.trim()) return null;
  
  const trimmed = dateStr.trim();
  
  // Try MM/DD/YYYY format first (common in exports)
  const mmddyyyyMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mmddyyyyMatch) {
    const [, month, day, year] = mmddyyyyMatch;
    const parsed = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    if (!isNaN(parsed)) {
      const yyyy = parsed.getFullYear();
      const mm = String(parsed.getMonth() + 1).padStart(2, '0');
      const dd = String(parsed.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }
  }
  
  // Try YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const parsed = new Date(trimmed);
    if (!isNaN(parsed)) {
      return trimmed;
    }
  }
  
  // Try general Date.parse
  const parsed = new Date(trimmed);
  if (!isNaN(parsed)) {
    const yyyy = parsed.getFullYear();
    const mm = String(parsed.getMonth() + 1).padStart(2, '0');
    const dd = String(parsed.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
  
  return null;
}

/**
 * Calculate due date by adding days to start date
 * 
 * @param {string} startDate - Start date in ISO format (YYYY-MM-DD)
 * @param {number} daysToAdd - Number of days to add (default: 30)
 * @returns {string} - Due date in ISO format (YYYY-MM-DD)
 */
function calculateDueDate(startDate, daysToAdd = 30) {
  const start = startDate ? new Date(startDate) : new Date();
  const due = new Date(start);
  due.setDate(due.getDate() + daysToAdd);
  return due.toISOString().split('T')[0];
}

/**
 * Persist new Lead Source to the leadSource collection if it doesn't exist
 * This ensures imported lead sources are available in dropdowns for future use
 * 
 * @param {string} leadSourceName - The lead source name to persist
 * @param {Set} existingLeadSources - Set of existing lead source names (for caching)
 * @returns {Promise<boolean>} - True if new source was added, false if already existed
 */
async function persistLeadSourceIfNew(leadSourceName, existingLeadSources) {
  if (!leadSourceName || !leadSourceName.trim()) return false;
  
  const normalizedName = leadSourceName.trim();
  
  // Skip if already known to exist
  if (existingLeadSources.has(normalizedName.toLowerCase())) {
    return false;
  }
  
  try {
    // Check if it exists in Firestore
    const sourceId = generateLeadSourceId(normalizedName);
    const docRef = doc(db, collectionPath('leadSource'), sourceId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      // Persist new lead source
      await setDoc(docRef, { 
        name: normalizedName,
        createdAt: serverTimestamp(),
        source: 'import'
      });
      console.log(`✅ Added new Lead Source to collection: "${normalizedName}"`);
      existingLeadSources.add(normalizedName.toLowerCase());
      return true;
    } else {
      // Already exists, add to cache
      existingLeadSources.add(normalizedName.toLowerCase());
      return false;
    }
  } catch (err) {
    console.warn(`⚠️ Failed to persist Lead Source "${normalizedName}":`, err);
    return false;
  }
}

/**
 * Check if opportunity already exists (duplicate detection)
 * Matches by: same customer + same sales rep + overlapping date range
 * 
 * @param {Array} existingOpportunities - Existing opportunities
 * @param {string} customerId - Customer ID to check
 * @param {string} salesRepUid - Sales rep UID
 * @param {string} startDate - Start date
 * @returns {Object|null} - Matching opportunity or null
 */
function findDuplicateOpportunity(existingOpportunities, customerId, salesRepUid, startDate, contactName, contactEmail, leadSource) {
  console.log('🔍 Checking for duplicates:', {
    existingCount: existingOpportunities?.length || 0,
    customerId,
    salesRepUid,
    startDate,
    contactName,
    contactEmail,
    leadSource
  });

  if (!customerId || !salesRepUid || !startDate) {
    console.log('❌ Missing required fields for duplicate check');
    return null;
  }

  const startDateObj = new Date(startDate);
  const rangeStart = new Date(startDateObj);
  rangeStart.setDate(rangeStart.getDate() - 7); // Look back 7 days
  const rangeEnd = new Date(startDateObj);
  rangeEnd.setDate(rangeEnd.getDate() + 7); // Look forward 7 days

  return existingOpportunities.find(opp => {
    // Must match customer and sales rep
    if (opp.customerId !== customerId) return false;
    if (opp.uid !== salesRepUid) return false;
    
    // Must match date range
    const oppStartDate = new Date(opp.startDate || opp.date);
    if (oppStartDate < rangeStart || oppStartDate > rangeEnd) return false;
    
    // Additional checks for more precise matching
    let matchScore = 0;
    
    // Check contact name match
    if (contactName && opp.contactName) {
      const nameMatch = contactName.toLowerCase().trim() === opp.contactName.toLowerCase().trim();
      if (nameMatch) matchScore += 2; // Strong indicator
    }
    
    // Check contact email match
    if (contactEmail && opp.contactEmail) {
      const emailMatch = contactEmail.toLowerCase().trim() === opp.contactEmail.toLowerCase().trim();
      if (emailMatch) matchScore += 3; // Very strong indicator
    }
    
    // Check lead source match
    if (leadSource && opp.leadSource) {
      const sourceMatch = leadSource.toLowerCase().trim() === opp.leadSource.toLowerCase().trim();
      if (sourceMatch) matchScore += 1;
    }
    
    // Consider it a duplicate if we have strong match indicators
    // Or if it's same customer+rep+date with no other data to distinguish
    return matchScore >= 2 || (!contactName && !contactEmail && !leadSource);
  });
}

/**
 * Validate CSV row data for required fields
 * 
 * @param {Object} row - CSV row data
 * @param {number} rowIndex - Row number (for error reporting)
 * @returns {Object} - { valid: boolean, errors: [] }
 */
function validateRow(row, rowIndex) {
  const errors = [];

  // Required fields - only Customer Name and Sales Rep are required
  if (!row['Customer Name']?.trim()) {
    errors.push(`Row ${rowIndex + 2}: Missing Customer Name`);
  }

  if (!row['Sales Rep']?.trim()) {
    errors.push(`Row ${rowIndex + 2}: Missing Sales Rep`);
  }

  // Contact info is now OPTIONAL - removed requirement for at least one contact method
  // This allows imports with just Customer Name + Sales Rep

  // Validate stage if provided
  const validStages = ['New', 'Qualified', 'Not Qualified', 'Proposal Sent', 'Closed Won', 'Closed Lost'];
  const stage = row['Stage']?.trim() || 'New'; // Default to New if not specified
  if (stage && !validStages.includes(stage)) {
    errors.push(`Row ${rowIndex + 2}: Invalid Stage "${stage}". Must be one of: ${validStages.join(', ')}`);
  }

  // Validate dates if provided (dates are optional - will default to today/+30 days)
  const startDate = row['Start Date']?.trim();
  if (startDate && !parseFlexibleDate(startDate)) {
    errors.push(`Row ${rowIndex + 2}: Invalid Start Date "${startDate}". Use format: MM/DD/YYYY or YYYY-MM-DD`);
  }

  const dueDate = row['Due Date']?.trim();
  if (dueDate && !parseFlexibleDate(dueDate)) {
    errors.push(`Row ${rowIndex + 2}: Invalid Due Date "${dueDate}". Use format: MM/DD/YYYY or YYYY-MM-DD`);
  }

  // Validate Potential Value - REQUIRED for all stages except "New"
  const value = row['Potential Value']?.trim();
  if (stage !== 'New' && (!value || value === '')) {
    errors.push(`Row ${rowIndex + 2}: Potential Value is required when Stage is "${stage}". It can only be left blank for "New" stage opportunities.`);
  }
  // If value is provided, validate format
  if (value && value !== '' && isNaN(parseFloat(value.replace(/[$,]/g, ''))) && !VALUE_RANGES.find(r => r.value === value)) {
    errors.push(`Row ${rowIndex + 2}: Invalid Potential Value "${value}". Must be a number or valid range (e.g., 5000-10000)`);
  }

  return {
    valid: errors.length === 0,
    errors: errors
  };
}

// ==================== FLEXIBLE HEADER NORMALIZATION ====================

/**
 * Maps common column name variants to canonical column names.
 * Keys must be lowercase — matching is case-insensitive.
 * Special values __first_name__ and __last_name__ are merged into 'Contact Name'.
 */
const COLUMN_ALIAS_MAP = {
  // Customer Name
  'company':              'Customer Name',
  'company name':         'Customer Name',
  'business name':        'Customer Name',
  'business':             'Customer Name',
  'account':              'Customer Name',
  'account name':         'Customer Name',
  'organization':         'Customer Name',
  'org':                  'Customer Name',
  'client':               'Customer Name',
  'client name':          'Customer Name',
  'brand':                'Customer Name',
  'brand name':           'Customer Name',
  'club':                 'Customer Name',
  'club name':            'Customer Name',
  'store':                'Customer Name',
  'store name':           'Customer Name',
  'retailer':             'Customer Name',
  'retailer name':        'Customer Name',

  // Contact Name (full-name column)
  'name':                 'Contact Name',
  'full name':            'Contact Name',
  'contact':              'Contact Name',
  'buyer name':           'Contact Name',
  'buyer':                'Contact Name',
  'pro':                  'Contact Name',
  'pro name':             'Contact Name',
  'buyer/pro':            'Contact Name',
  'buyer / pro':          'Contact Name',
  'attendee':             'Contact Name',
  'attendee name':        'Contact Name',
  'person':               'Contact Name',
  'guest':                'Contact Name',

  // Split first/last name — merged into 'Contact Name' automatically
  'first name':           '__first_name__',
  'firstname':            '__first_name__',
  'first':                '__first_name__',
  'fname':                '__first_name__',
  'given name':           '__first_name__',
  'last name':            '__last_name__',
  'lastname':             '__last_name__',
  'last':                 '__last_name__',
  'lname':                '__last_name__',
  'surname':              '__last_name__',
  'family name':          '__last_name__',

  // Contact Email
  'email':                'Contact Email',
  'email address':        'Contact Email',
  'e-mail':               'Contact Email',
  'e mail':               'Contact Email',
  'emailaddress':         'Contact Email',
  'buyer email':          'Contact Email',
  'pro email':            'Contact Email',
  'work email':           'Contact Email',
  'business email':       'Contact Email',
  'contact email address':'Contact Email',

  // Contact Phone
  'phone':                'Contact Phone',
  'phone number':         'Contact Phone',
  'phonenumber':          'Contact Phone',
  'telephone':            'Contact Phone',
  'tel':                  'Contact Phone',
  'mobile':               'Contact Phone',
  'mobile number':        'Contact Phone',
  'cell':                 'Contact Phone',
  'cell phone':           'Contact Phone',
  'work phone':           'Contact Phone',
  'buyer phone':          'Contact Phone',
  'buyer cell':           'Contact Phone',
  'direct':               'Contact Phone',
  'direct phone':         'Contact Phone',

  // Contact Title
  'title':                'Contact Title',
  'job title':            'Contact Title',
  'jobtitle':             'Contact Title',
  'position':             'Contact Title',
  'role':                 'Contact Title',
  'job':                  'Contact Title',
  'occupation':           'Contact Title',

  // Contact Focus
  'focus':                'Contact Focus',
  'contact focus':        'Contact Focus',
  'business focus':       'Contact Focus',
  'sport':                'Contact Focus',
  'sports':               'Contact Focus',
  'segment':              'Contact Focus',
  'category':             'Contact Focus',
  'market':               'Contact Focus',

  // Location
  'city':                 'Customer City',
  'town':                 'Customer City',
  'state':                'Customer State',
  'province':             'Customer State',
  'state/province':       'Customer State',
  'region':               'Customer State',
  'country':              'Customer Country',
  'nation':               'Customer Country',
  'zip':                  '__ignored__',
  'zip code':             '__ignored__',
  'postal code':          '__ignored__',

  // Lead Source
  'source':               'Lead Source',
  'how did you hear':     'Lead Source',
  'campaign':             'Lead Source',
  'event':                'Lead Source',
  'event name':           'Lead Source',
  'show':                 'Lead Source',
  'show name':            'Lead Source',
  'trade show':           'Lead Source',
  'channel':              'Lead Source',
  'marketing source':     'Lead Source',

  // Stage
  'status':               'Stage',
  'pipeline stage':       'Stage',
  'opportunity stage':    'Stage',
  'opp stage':            'Stage',

  // Potential Value
  'value':                'Potential Value',
  'estimated value':      'Potential Value',
  'budget':               'Potential Value',
  'deal size':            'Potential Value',
  'amount':               'Potential Value',
  'potential':            'Potential Value',
  'estimated amount':     'Potential Value',
  'deal value':           'Potential Value',

  // Dates
  'date':                 'Start Date',
  'open date':            'Start Date',
  'created date':         'Start Date',
  'creation date':        'Start Date',
  'meeting date':         'Start Date',
  'visit date':           'Start Date',
  'close date':           'Due Date',
  'closing date':         'Due Date',
  'deadline':             'Due Date',
  'follow up date':       'Due Date',
  'follow-up date':       'Due Date',
  'target date':          'Due Date',
  'target close date':    'Due Date',

  // Sales Rep
  'rep':                  'Sales Rep',
  'assigned to':          'Sales Rep',
  'owner':                'Sales Rep',
  'agent':                'Sales Rep',
  'salesperson':          'Sales Rep',
  'sales person':         'Sales Rep',
  'account manager':      'Sales Rep',
  'am':                   'Sales Rep',
  'territory rep':        'Sales Rep',
  'territory manager':    'Sales Rep',

  // Notes
  'note':                 'Notes',
  'comments':             'Notes',
  'comment':              'Notes',
  'description':          'Notes',
  'additional info':      'Notes',
  'additional information':'Notes',
  'remarks':              'Notes',
  'memo':                 'Notes',
};

/**
 * Normalize a single CSV row's keys to canonical column names.
 * Handles case-insensitive matching, common aliases, and merging
 * split "First Name" + "Last Name" columns into a single "Contact Name".
 *
 * @param {Object} row - Raw parsed CSV row
 * @returns {Object} Row with canonical keys
 */
export function normalizeImportRow(row) {
  const normalized = {};
  let firstName = '';
  let lastName = '';

  for (const [key, value] of Object.entries(row)) {
    const keyLower = key.trim().toLowerCase();
    const canonical = COLUMN_ALIAS_MAP[keyLower];

    if (!canonical) {
      // Not in alias map — keep original key so canonical columns are preserved as-is
      if (!(key.trim() in normalized)) {
        normalized[key.trim()] = value;
      }
      continue;
    }

    if (canonical === '__first_name__') {
      firstName = (value || '').trim();
    } else if (canonical === '__last_name__') {
      lastName = (value || '').trim();
    } else if (canonical === '__ignored__') {
      // Silently skip unneeded columns (zip, postal code, etc.)
    } else {
      // Only set if this canonical key hasn't already been claimed
      // (so an exact 'Contact Name' column beats an alias like 'Buyer')
      if (!(canonical in normalized)) {
        normalized[canonical] = value;
      }
    }
  }

  // Merge First Name + Last Name → Contact Name (only if a full-name column wasn't provided)
  if ((firstName || lastName) && !('Contact Name' in normalized)) {
    normalized['Contact Name'] = [firstName, lastName].filter(Boolean).join(' ');
  }

  return normalized;
}

/**
 * Main import function with fuzzy customer matching
 * Processes CSV data and creates opportunities + placeholder customers
 * 
 * @param {Array} csvData - Array of CSV row objects
 * @param {Array} existingCustomers - Existing customers for matching
 * @param {Array} existingOpportunities - Existing opportunities for duplicate detection
 * @param {Array} users - All users for sales rep assignment
 * @param {Object} currentUser - Current user performing import
 * @param {Function} onProgress - Optional progress callback (current, total, status)
 * @returns {Object} - Import results { added, updated, skipped, customersCreated, customersMatched, errors }
 */
export async function importOpportunitiesWithCustomerMatch(
  csvData,
  existingCustomers,
  existingOpportunities,
  users,
  currentUser,
  onProgress = null
) {
  const results = {
    added: 0,
    updated: 0,
    skipped: 0,
    duplicatesSkipped: 0,
    customersCreated: 0,
    customersMatched: 0,
    leadSourcesAdded: 0,
    errors: [],
    warnings: [],
    matchDetails: [], // Track detailed match info for email summary
    rowsNeedingReview: [] // Track which rows created temp customers for review
  };

  // Cache for existing lead sources to minimize Firestore reads
  const existingLeadSources = new Set();
  try {
    const leadSourcesSnap = await getDocs(collection(db, collectionPath('leadSource')));
    leadSourcesSnap.docs.forEach(doc => {
      const name = doc.data().name;
      if (name) existingLeadSources.add(name.toLowerCase());
    });
    console.log(`📋 Loaded ${existingLeadSources.size} existing lead sources`);
  } catch (err) {
    console.warn('⚠️ Failed to load existing lead sources:', err);
  }

  // Load standalone contacts collection for customer matching (contacts collection first, then embedded)
  let standaloneContacts = [];
  try {
    const contactsSnap = await getDocs(collection(db, collectionPath('contacts')));
    standaloneContacts = contactsSnap.docs.map(d => {
      const c = d.data();
      return {
        id: d.id,
        customerId: c.customerId || c.contactCustomerId || '',
        firstName: c.firstName || '',
        lastName: c.lastName || '',
        email: (c.email || '').toLowerCase(),
        phone: c.phone || '',
      };
    });
    console.log(`📋 Loaded ${standaloneContacts.length} standalone contacts for matching`);
  } catch (err) {
    console.warn('⚠️ Failed to load standalone contacts:', err);
  }

  // Normalize all row headers first (maps aliases → canonical column names,
  // merges split First Name + Last Name → Contact Name, etc.)
  const normalizedData = csvData.map(row => normalizeImportRow(row));

  // Filter out truly empty rows (rows where all fields are empty/whitespace)
  // This handles cases where Excel saves CSV with trailing empty rows
  // Also filter out the documentation/instruction row from the template
  const cleanedData = normalizedData.filter(row => {
    // Check if at least one field has actual content
    const hasContent = Object.values(row).some(val => val && String(val).trim() !== '');
    if (!hasContent) return false;
    
    // Filter out documentation row (starts with "---", contains "VALID" + "VALUES", or has hint patterns)
    const customerName = row['Customer Name']?.trim() || '';
    if (customerName.startsWith('---') || 
        customerName.toUpperCase().includes('VALID') && customerName.toUpperCase().includes('VALUES')) {
      console.log('📋 Skipping documentation row:', customerName);
      return false;
    }
    
    // Also detect instruction rows by checking if Stage contains a comma-separated list of all valid stages
    const stage = row['Stage']?.trim() || '';
    if (stage.includes('New') && stage.includes('Qualified') && stage.includes('Closed Won') && stage.includes('Closed Lost')) {
      console.log('📋 Skipping documentation row (detected by stage list):', customerName);
      return false;
    }
    
    return true;
  });
  
  console.log('📋 Filtered empty rows:', {
    originalRows: csvData.length,
    cleanedRows: cleanedData.length,
    removedEmptyRows: csvData.length - cleanedData.length
  });
  
  // Validate all rows first
  const validationErrors = [];
  cleanedData.forEach((row, idx) => {
    const validation = validateRow(row, idx);
    if (!validation.valid) {
      validationErrors.push(...validation.errors);
    }
  });

  console.log('📋 Validation complete:', {
    totalRows: cleanedData.length,
    validationErrors: validationErrors.length,
    sampleRow: cleanedData[0]
  });

  if (validationErrors.length > 0) {
    console.error('❌ Validation failed:', validationErrors);
    results.errors = validationErrors;
    return results;
  }

  // Process each row
  for (let i = 0; i < cleanedData.length; i++) {
    const row = cleanedData[i];
    const rowNum = i + 2; // CSV row number (accounting for header)

    try {
      if (onProgress) {
        onProgress(i + 1, csvData.length, `Processing row ${rowNum}...`);
      }

      // 1. Find or create customer
      let customerId = null;
      let customerMatch = null;
      const customerName = row['Customer Name']?.trim();
      // Customer location fields (optional)
      const customerCity = row['Customer City']?.trim() || '';
      const customerState = row['Customer State']?.trim() || '';
      const customerCountry = row['Customer Country']?.trim() || '';

      if (!customerName) {
        results.errors.push({ row: rowNum, error: 'Missing Customer Name', data: row });
        results.skipped++;
        continue;
      }

      // Only auto-match on 100% certainty (exact match on ANY of 6 fields)
      // All other cases create temp customer for manual review in Customer Review UI
      const contactName = row['Contact Name']?.trim();
      const contactTitle = row['Contact Title']?.trim() || '';
      const contactEmail = row['Contact Email']?.trim();
      const contactPhone = row['Contact Phone']?.trim();
      
      // For matching, we no longer use customer email/phone (removed from import)
      // Match only via customer name or contact fields
      customerMatch = findExactCustomerMatch(
        customerName,
        '', // customerEmail removed from import
        '', // customerPhone removed from import
        contactName,
        contactEmail,
        contactPhone,
        existingCustomers,
        standaloneContacts
      );

      console.log('Customer matching attempt:', {
        searchName: customerName,
        searchContactEmail: contactEmail,
        searchContactPhone: contactPhone,
        totalcustomers: existingCustomers.length,
        matchFound: !!customerMatch,
        match: customerMatch
      });

      if (customerMatch && customerMatch.confidence === 100) {
        // Found 100% certain match - auto-link to existing customer
        customerId = customerMatch.customer.id;
        results.customersMatched++;
        
        console.log(`✅ Auto-matched to existing customer: ${customerMatch.customer.customerName} (${customerMatch.matchType})`);
        
        // **CRITICAL**: Check if this is a previously merged customer
        // If yes, flag it for review to prevent duplicate customer entries
        if (customerMatch.customer.mergedIntocustomerId) {
          try {
            const { updateDoc } = await import('firebase/firestore');
            const customerRef = doc(db, collectionPath('customers'), customerMatch.customer.id);
            await updateDoc(customerRef, { 
              needsReview: true,
              lastImportAttempt: new Date(),
              lastImportAttemptBy: currentUser?.uid || ''
            });
            
            results.warnings.push({
              row: rowNum,
              message: `⚠️ MERGED CUSTOMER DETECTED: "${customerMatch.customer.customerName}" was previously merged into another customer. Flagged for review to prevent duplicates.`,
              matchedCustomer: customerMatch.customer.customerName,
              isMerged: true,
              mergedInto: customerMatch.customer.mergedIntocustomerId
            });
            
            console.log(`🚨 MERGED CUSTOMER detected: ${customerMatch.customer.customerName} (merged into ${customerMatch.customer.mergedIntocustomerId})`);
          } catch (updateErr) {
            console.error('Failed to flag MERGED CUSTOMER for review:', updateErr);
            results.warnings.push({
              row: rowNum,
              message: `⚠️ Matched MERGED CUSTOMER "${customerMatch.customer.customerName}" but failed to update review flag: ${updateErr.message}`,
              matchedCustomer: customerMatch.customer.customerName
            });
          }
        }
        
        // Log 100% match with correct match type description
        const matchMethodDescriptions = {
          'exact_customer_name': 'customer name',
          'exact_customer_email': 'customer email',
          'exact_customer_phone': 'customer phone',
          'exact_contact_name': 'contact name',
          'exact_contact_email': 'contact email',
          'exact_contact_phone': 'contact phone'
        };
        const matchMethod = matchMethodDescriptions[customerMatch.matchType] || customerMatch.matchType;
        
        // Build match details for message
        let matchedVia = matchMethod;
        if (customerMatch.matchType === 'exact_contact_email') {
          matchedVia = `contact email (${contactEmail})`;
        } else if (customerMatch.matchType === 'exact_contact_name') {
          matchedVia = `contact name (${contactName})`;
        } else if (customerMatch.matchType === 'exact_contact_phone') {
          matchedVia = `contact phone (${contactPhone})`;
        }
        
        results.warnings.push({
          row: rowNum,
          message: `Row ${rowNum}: Auto-matched to existing customer "${customerMatch.customer.customerName}" via ${matchedVia}`,
          matchedCustomer: customerMatch.customer.customerName,
          isMatch: true
        });
        
        // Add detailed match info for email summary
        const matchDetail = {
          row: rowNum,
          csvName: customerName,
          customerName: customerMatch.customer.customerName,
          email: contactEmail || '',
          phone: contactPhone || '',
          matchType: customerMatch.matchType,
          confidence: customerMatch.confidence
        };
        
        // Add contact info if this was a contact-based match
        if (customerMatch.matchType.includes('contact')) {
          matchDetail.contactName = contactName || '';
          matchDetail.contactEmail = contactEmail || '';
          matchDetail.contactPhone = contactPhone || '';
        }
        
        results.matchDetails.push(matchDetail);
      } else {
        // No 100% match - create temp customer flagged for review
        // User will manually match in Customer Review UI
        const salesRepName = row['Sales Rep']?.trim();
        customerId = await createPlaceholderCustomer(row, currentUser, salesRepName, { customerCity, customerState, customerCountry });
        results.customersCreated++;
        results.rowsNeedingReview.push(rowNum);
        
        console.log(`📝 Created temp customer for review: ${customerName}`);
        
        // Add detailed match info for email summary (new customer)
        const capitalizedName = capitalizeCustomerName(customerName);
        results.matchDetails.push({
          row: rowNum,
          customerName: capitalizedName,
          email: contactEmail || '',
          phone: contactPhone || '',
          matchType: 'new_customer'
        });
        
        // Add to tracking for subsequent rows in same import
        existingCustomers.push({
          id: customerId,
          customerName: capitalizedName,
          customerEmail: '',
          customerPhone: '',
          needsReview: true
        });
      }

      // 1.5 Create contact if Buyer/Pro name is provided
      // ONLY add contacts when we have HIGH CONFIDENCE matches (email/phone)
      // Customer name matches are NOT reliable enough to auto-add contacts
      const highConfidenceMatchTypes = [
        'exact_customer_email',
        'exact_customer_phone', 
        'exact_contact_email',
        'exact_contact_phone'
      ];
      
      const shouldAddContact = customerMatch && highConfidenceMatchTypes.includes(customerMatch.matchType);
      let resolvedContactId = null;

      if (shouldAddContact) {
        try {
          const contactResult = await createContactFromImport(row, customerId, currentUser);
          if (contactResult) {
            resolvedContactId = contactResult;
            console.log(`✅ Contact added/found for Customer ${customerId} (matched via ${customerMatch.matchType}), contactId: ${resolvedContactId}`);
          }
        } catch (contactErr) {
          console.error('Error creating contact:', contactErr);
          results.warnings.push({
            row: rowNum,
            message: `Opportunity will be created but contact failed: ${contactErr.message}`
          });
        }
      } else if (customerMatch && customerMatch.matchType === 'exact_customer_name') {
        // Matched via customer name only - DON'T add contacts, might be wrong customer
        console.log(`⚠️ Skipping contact creation - matched only via customer name "${customerMatch.customer.customerName}"`);
      } else if (!customerMatch) {
        // New customer was created - add contact to the new customer
        try {
          const contactResult = await createContactFromImport(row, customerId, currentUser);
          if (contactResult) {
            resolvedContactId = contactResult;
            console.log(`✅ Contact added to new Customer ${customerId}, contactId: ${resolvedContactId}`);
          }
        } catch (contactErr) {
          console.error('Error creating contact:', contactErr);
          results.warnings.push({
            row: rowNum,
            message: `Opportunity will be created but contact failed: ${contactErr.message}`
          });
        }
      }

      // 2. Find sales rep
      const salesRepName = row['Sales Rep']?.trim();
      const salesRep = findUserByName(salesRepName, users);

      if (!salesRep) {
        results.errors.push({
          row: rowNum,
          error: `Sales Rep "${salesRepName}" not found in system`,
          data: row
        });
        results.skipped++;
        continue;
      }

      // 3. Prepare opportunity data
      const startDate = parseFlexibleDate(row['Start Date']) || new Date().toISOString().split('T')[0];
      const dueDate = parseFlexibleDate(row['Due Date']) || calculateDueDate(startDate, 30);
      // contactName, contactEmail, contactPhone already declared above for matching
      const leadSource = row['Lead Source']?.trim() || 'Import';

      // 4. Check for duplicate opportunity (enhanced with contact/email/source matching)
      const duplicate = findDuplicateOpportunity(
        existingOpportunities,
        customerId,
        salesRep.uid,
        startDate,
        contactName,
        contactEmail,
        leadSource
      );

      if (duplicate) {
        results.warnings.push({
          row: rowNum,
          message: `❌ DUPLICATE SKIPPED: ${customerName} with ${salesRepName} on ${startDate}. Existing opportunity #${duplicate.opportunityNumberLabel || duplicate.id}`,
          existingOppId: duplicate.id,
          isDuplicate: true
        });
        results.skipped++;
        results.duplicatesSkipped++;
        continue; // Skip creating this duplicate
      }

      // 5. Generate opportunity number using transaction
      let opportunityNumber = 0;
      let oppDocId = '';
      try {
        const counterRef = doc(db, collectionPath('counters'), 'opportunityNumber');
        await runTransaction(db, async (transaction) => {
          const counterDoc = await transaction.get(counterRef);
          if (!counterDoc.exists()) {
            transaction.set(counterRef, { next: 2 });
            opportunityNumber = 1;
          } else {
            const next = counterDoc.data().next || 1;
            opportunityNumber = next;
            transaction.update(counterRef, { next: next + 1 });
          }
        });
        oppDocId = generateOpportunityId(opportunityNumber);
      } catch (err) {
        console.error('Error generating opportunity number:', err);
        oppDocId = `TEMP-${Date.now()}`; // Fallback
      }

      // 6. Create opportunity
      // Potential Value is optional for "New" stage, required for others (validated earlier)
      const potentialValue = row['Potential Value']?.trim();
      console.log(`[Import Debug] Potential Value from CSV: "${potentialValue}" (length: ${potentialValue?.length || 0})`);
      const computedValue = (potentialValue && potentialValue !== '') ? normalizeValueToRange(potentialValue) : '';
      console.log(`[Import Debug] Computed value for opportunity: "${computedValue}"`);
      const opportunityData = {
        opportunityNumber: opportunityNumber,
        opportunityNumberLabel: oppDocId, // Human-readable ID (OPP-#)
        customerId: customerId,
        // CRITICAL: Use matched customer's name for 100% matches, CSV name for temp customers
        customerName: customerMatch?.customer?.customerName || capitalizeCustomerName(customerName),
        stage: row['Stage']?.trim() || 'New',
        leadSource: row['Lead Source']?.trim() || 'Import',
        value: computedValue, // Empty if not provided
        startDate: convertDateForSave(startDate),
        date: convertDateForSave(startDate), // Legacy field
        dueDate: convertDateForSave(dueDate),
        uid: salesRep.uid,
        importBatch: new Date().toISOString(),
        createdAt: serverTimestamp(),
        createdBy: currentUser?.uid || '',
        updatedAt: serverTimestamp(),
        // Optional fields
        closedDate: row['Closed Date'] ? convertDateForSave(parseFlexibleDate(row['Closed Date'])) : null,
        ...(contactName && { contactName: sanitizeString(contactName, 200) }),
        ...(contactTitle && { contactTitle: sanitizeString(contactTitle, 100) }),
        ...(contactEmail && { contactEmail: contactEmail.toLowerCase() }),
        ...(contactPhone && { contactPhone: sanitizeString(contactPhone, 20) }),
        ...(resolvedContactId && { contactId: resolvedContactId })
      };

      // Use OPP-# format as document ID
      const oppRef = doc(db, collectionPath('opportunities'), oppDocId);
      await setDoc(oppRef, opportunityData);

      // Log with ACTUAL customer name used (from match or capitalized CSV name)
      const actualCustomerName = customerMatch?.customer?.customerName || capitalizeCustomerName(customerName);
      console.log(`✅ Created opportunity ${oppDocId} for Customer "${actualCustomerName}"` + 
        (customerMatch ? ` (matched from CSV: "${customerName}")` : ''));

      // 7. Log opportunity creation FIRST (before notes, so log timestamps are in logical order)
      await logEntityChanges({
        oldObj: null,
        newObj: { ...opportunityData, id: oppDocId },
        fields: ['opportunityNumber', 'customerId', 'stage', 'leadSource', 'value', 'startDate', 'dueDate', 'contactName', 'contactEmail'],
        entityType: 'Opportunity',
        entityId: oppDocId,
        user: {
          uid: currentUser?.uid || '',
          email: currentUser?.email || '',
          displayName: currentUser?.displayName || '',
          role: currentUser?.role || 'user'
        },
        action: 'Created'
      });

      // 8. Create note if Notes field is present (using unified notes collection)
      const notesText = row['Notes']?.trim();
      if (notesText) {
        try {
          const { createAssociatedNote } = await import('./associationUtils');
          await createAssociatedNote(
            {
              text: sanitizeString(notesText, 5000),
              sourceEntityType: 'opportunity',
              sourceEntityId: oppDocId,
              tags: ['import']
            },
            {
              uid: currentUser?.uid || '',
              displayName: currentUser?.displayName || currentUser?.email || 'System',
              email: currentUser?.email || ''
            },
            {
              opportunityId: oppDocId,
              customerId: customerId
            }
          );
        } catch (noteErr) {
          console.error('Error creating note:', noteErr);
          results.warnings.push({
            row: rowNum,
            message: `Opportunity created but note failed: ${noteErr.message}`
          });
        }
      }

      // 9. Persist new Lead Source if it doesn't exist in collection
      if (leadSource && leadSource !== 'Import') {
        const wasAdded = await persistLeadSourceIfNew(leadSource, existingLeadSources);
        if (wasAdded) {
          results.leadSourcesAdded++;
        }
      }

      results.added++;

    } catch (error) {
      console.error(`Error processing row ${rowNum}:`, error);
      results.errors.push({
        row: rowNum,
        error: error.message,
        data: row
      });
      results.skipped++;
    }
  }

  if (onProgress) {
    onProgress(csvData.length, csvData.length, 'Import complete!');
  }

  return results;
}

/**
 * Generate import summary message for user feedback
 * 
 * @param {Object} results - Import results object
 * @returns {string} - Human-readable summary
 */
export function generateImportSummary(results) {
  const parts = [];

  if (results.added > 0) {
    parts.push(`✅ Added ${results.added} opportunit${results.added !== 1 ? 'ies' : 'y'}`);
  }

  if (results.duplicatesSkipped > 0) {
    parts.push(`🔄 Skipped ${results.duplicatesSkipped} duplicate${results.duplicatesSkipped !== 1 ? 's' : ''}`);
  }

  // Show customers matched
  if (results.customersMatched > 0) {
    parts.push(`🔗 Matched ${results.customersMatched} existing ${results.customersMatched !== 1 ? 'customers' : 'customer'}`);
  }

  // Show customers created (need review)
  if (results.customersCreated > 0) {
    parts.push(`🏢 ${results.customersCreated} ${results.customersCreated !== 1 ? 'customers' : 'customer'} need review`);
  }

  // Show warnings separately (not tied to customers matched)
  if (results.warnings.length > 0) {
    parts.push(`⚠️ ${results.warnings.length} warning${results.warnings.length !== 1 ? 's' : ''}`);
  }

  if (results.skipped > 0 && results.duplicatesSkipped === 0) {
    parts.push(`⚠️ Skipped ${results.skipped} row${results.skipped !== 1 ? 's' : ''} with errors`);
  }

  if (results.errors.length > 0) {
    parts.push(`❌ ${results.errors.length} error${results.errors.length !== 1 ? 's' : ''}`);
  }

  return parts.join(' | ');
}

/**
 * Export opportunities to CSV template format
 * Generates template with sample data and instructions
 * 
 * @returns {Array} - Array of CSV rows
 */
export function generateImportTemplate() {
  // Build value range options string for documentation
  const valueRangeOptions = VALUE_RANGES.map(r => r.value).join(', ');
  
  return [
    // Header row
    [
      'Customer Name',
      'Customer City',
      'Customer State',
      'Customer Country',
      'Contact Name',
      'Contact Title',
      'Contact Email',
      'Contact Phone',
      'Contact Focus',
      'Lead Source',
      'Stage',
      'Potential Value',
      'Start Date',
      'Due Date',
      'Sales Rep',
      'Notes'
    ],
    // Sample row with example data - user should replace this with real data
    [
      'Sofibella Test Account',
      'Pompano Beach',
      'FL',
      'USA',
      'John Doe',
      'Buyer',
      'buyer@sofibellawear.com',
      '555-987-6543',
      'Golf',
      'PGA Show 202X',
      'New',
      '', // Potential Value is optional
      '', // Start Date auto-fills to today if empty
      '', // Due Date auto-fills to +30 days if empty
      'Jane Smith', // Sales Rep must match existing user DisplayName
      'Met at PGA Show booth'
    ],
    // Documentation row explaining valid values
    [
      'VALID ROW VALUES --- (Also accepted: Company, Business, Account, Organization, Brand, Club)',
      'Optional also: City',
      'Optional also: State, Province',
      'Optional also: Country',
      'Optional also: First Name+Last Name columns, Buyer, Buyer/Pro, Attendee',
      'Optional also: Job Title, Position, Role',
      'Optional also: Email, Email Address, Work Email, Buyer Email',
      'Optional also: Phone, Mobile, Cell, Tel, Direct',
      'Optional comma-separated also: Focus, Business Focus, Sport, Segment',
      'Optional also: Source, Event Name, Show Name, Trade Show, Campaign',
      'New, Qualified, Not Qualified, Proposal Sent, Closed Won, Closed Lost (also: Status)',
      `${valueRangeOptions} (Optional if stage is "New" also: Value, Budget, Amount, Deal Size)`,
      'MM/DD/YYYY (Optional defaults to today; also: Date, Meeting Date, Visit Date)',
      'MM/DD/YYYY (Optional defaults to +30 days; also: Close Date, Deadline, Target Date)',
      'REQUIRED: must match a user Display Name (also: Rep, Owner, Account Manager, AM)',
      'Optional also: Note, Comments, Description, Remarks'
    ]
    // Do NOT include empty row - users will add their own rows or replace the sample
  ];
}


